import React, {useEffect, useState} from 'react';
import {Grid} from '@mui/material';
import {DatePicker, LocalizationProvider, type PickerChangeHandlerContext} from '@mui/x-date-pickers';
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment';
import type {DateValidationError} from '@mui/x-date-pickers/models';
import {PickersActionBar, type PickersActionBarProps} from '@mui/x-date-pickers/PickersActionBar';
import moment, {type Moment, type MomentInput} from 'moment';
import {DateFormats} from '../../constants/securspace-constants';
import AvailabilityPickersDay, {
  type AvailabilityByDate,
  type AvailabilityPickersDayProps,
  type SelectedDateRange,
  availabilityCalendarLayoutSx,
  availabilityDateKey,
} from './AvailabilityPickersDay';
import AvailabilityPickersLayout, {type AvailabilityPickersLayoutProps} from './AvailabilityPickersLayout';

interface AvailabilityFromToDatePickerProps {
  fromDate: MomentInput;
  setFromDate: (date: Moment | null) => void;
  toDate: MomentInput;
  setToDate: (date: Moment | null) => void;
  /**
   * Puts back both dates as they were when a calendar opened (its Cancel), as set by the user rather than adjusted
   * like a picked date (e.g. To moved past a later From).
   */
  restoreDates: (fromDate: Moment, toDate: Moment) => void;
  /** Per-day location availability; FULL days can't be picked. */
  availability: AvailabilityByDate;
  /** Called with each month the calendars navigate to, so its availability can be loaded. */
  onCalendarMonthChange?: (month: Moment) => void;
  disablePast?: boolean;
  showEndDate?: boolean;
  disabled?: boolean;
  minToDate?: MomentInput;
}

const getErrorMessage = (error: DateValidationError | null, dayIsKnown: boolean): string => {
  switch (error) {
    case 'maxDate':
    case 'minDate':
      return 'Date cannot be before start date';
    case 'invalidDate':
      return 'Your date is not valid';
    case 'shouldDisableDate':
      return dayIsKnown ? 'No spaces available on this date' : 'Checking availability for this date';
    default:
      return '';
  }
};

interface AvailabilityDatePickerProps {
  label: string;
  className: string;
  heading: string;
  subheading?: string;
  value: Moment;
  onChange: (date: Moment | null) => void;
  minDate?: Moment;
  selectedRange: SelectedDateRange | null;
  onOpenCalendar: () => void;
  onCancelChanges: () => void;
  availability: AvailabilityByDate;
  onCalendarMonthChange?: (month: Moment) => void;
  disablePast: boolean;
  disabled: boolean;
}

interface AvailabilityActionBarProps extends PickersActionBarProps {
  onConfirm?: () => void;
  onCancelChanges?: () => void;
}

/** Cancel / Confirm, closing the calendar that otherwise stays open while days are picked. */
const AvailabilityActionBar = ({onConfirm, onCancelChanges, ...props}: AvailabilityActionBarProps) => (
  <PickersActionBar
    {...props}
    actions={['cancel', 'accept']}
    onAccept={onConfirm ?? props.onAccept}
    onCancel={onCancelChanges ?? props.onCancel}
  />
);

const dayKey = (date: Moment): string => (date.isValid() ? availabilityDateKey(date) : '');
const monthKey = (month: Moment): string => availabilityDateKey(month.clone().startOf('month'));

/** The booking's days to highlight: From through To, or just From without a usable end date. */
const getSelectedRange = (startDate: Moment, endDate: Moment | null): SelectedDateRange | null => {
  if (!startDate.isValid()) return null;
  const start = availabilityDateKey(startDate);
  if (!endDate || !endDate.isValid() || endDate.isBefore(startDate, 'day')) return {start, end: start};
  return {start, end: availabilityDateKey(endDate)};
};

const AvailabilityDatePicker = ({
                                  label,
                                  className,
                                  heading,
                                  subheading,
                                  value,
                                  onChange,
                                  minDate,
                                  selectedRange,
                                  onOpenCalendar,
                                  onCancelChanges,
                                  availability,
                                  onCalendarMonthChange,
                                  disablePast,
                                  disabled,
                                }: AvailabilityDatePickerProps) => {
  const [error, setError] = useState<DateValidationError | null>(null);
  // The last date entered; a rejected one isn't passed on, so `value` can still be the previous date.
  const [enteredDate, setEnteredDate] = useState<Moment | null>(null);
  const [visibleMonth, setVisibleMonth] = useState<Moment | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const showMonth = (month: Moment) => {
    setVisibleMonth(month);
    onCalendarMonthChange?.(month);
  };
  // The API returns every day of a requested month, so a missing day means its month hasn't loaded yet.
  const dayIsKnown = (day: Moment) => availability[availabilityDateKey(day)] !== undefined;
  const handleChange = (date: Moment | null, context: PickerChangeHandlerContext<DateValidationError>) => {
    setEnteredDate(date);
    if (context.validationError) return;
    onChange(moment(date, DateFormats.DAY));
  };
  const handleOpen = () => {
    setIsOpen(true);
    onOpenCalendar();
    showMonth((value.isValid() ? value : moment()).clone().startOf('month'));
  };
  const close = () => setIsOpen(false);
  const cancelChanges = () => {
    onCancelChanges();
    close();
  };
  const checkedDate = enteredDate?.isValid() ? enteredDate : value;
  const valueKey = value.isValid() ? availabilityDateKey(value) : '';
  useEffect(() => setEnteredDate(null), [valueKey]);

  return (
    <DatePicker
      label={label}
      className={className}
      disabled={disabled}
      disablePast={disablePast}
      format={DateFormats.DAY}
      value={value}
      minDate={minDate}
      onChange={handleChange}
      // Picked days apply right away; the calendar stays open until Confirm, Cancel or a click away.
      closeOnSelect={false}
      onError={setError}
      open={isOpen}
      onOpen={handleOpen}
      onClose={close}
      onMonthChange={showMonth}
      onYearChange={showMonth}
      loading={visibleMonth !== null && !availability[monthKey(visibleMonth)]}
      shouldDisableDate={(day: Moment) => !dayIsKnown(day) || availability[availabilityDateKey(day)].status === 'FULL'}
      slots={{day: AvailabilityPickersDay, layout: AvailabilityPickersLayout, actionBar: AvailabilityActionBar}}
      slotProps={{
        day: {availability, selectedRange} as Partial<AvailabilityPickersDayProps>,
        layout: {sx: availabilityCalendarLayoutSx, heading, subheading} as Partial<AvailabilityPickersLayoutProps>,
        actionBar: {onConfirm: close, onCancelChanges: cancelChanges} as Partial<AvailabilityActionBarProps>,
        textField: {variant: 'standard', helperText: getErrorMessage(error, checkedDate.isValid() && dayIsKnown(checkedDate))},
      }}
      localeText={{toolbarTitle: heading, okButtonLabel: 'Confirm'}}
    />
  );
};

/**
 * From/To pickers whose larger calendar marks each day's availability at the location, with a legend (#3271).
 * Both calendars show the whole From–To range. Picked days apply right away and the calendar stays open until
 * Confirm; Cancel restores both dates as they were when it opened. Used in place of
 * {@link FromToDatePicker} when the location is held to capacity. Days whose month hasn't loaded can't be picked,
 * and dates the calendar rejects (e.g. typed FULL days) aren't passed on.
 */
const AvailabilityFromToDatePicker = ({
                                        fromDate,
                                        setFromDate,
                                        toDate,
                                        setToDate,
                                        restoreDates,
                                        availability,
                                        onCalendarMonthChange,
                                        disablePast = false,
                                        showEndDate = true,
                                        disabled = false,
                                        minToDate,
                                      }: AvailabilityFromToDatePickerProps) => {
  // Both dates when a calendar opened; picking From can also move To, so Cancel restores them together.
  const [datesAtOpen, setDatesAtOpen] = useState<{fromDate: Moment; toDate: Moment} | null>(null);
  const startDate = moment(fromDate, DateFormats.DAY);
  const endDate = moment(toDate, DateFormats.DAY);
  const endSubheading = startDate.isValid() ? `Starting ${startDate.format('MMM D, YYYY')}` : undefined;
  const sharedProps = {
    selectedRange: getSelectedRange(startDate, showEndDate ? endDate : null),
    onOpenCalendar: () => setDatesAtOpen({fromDate: startDate.clone(), toDate: endDate.clone()}),
    onCancelChanges: () => {
      if (!datesAtOpen) return;
      const changed = dayKey(datesAtOpen.fromDate) !== dayKey(startDate) || dayKey(datesAtOpen.toDate) !== dayKey(endDate);
      if (changed) restoreDates(datesAtOpen.fromDate.clone(), datesAtOpen.toDate.clone());
    },
    availability,
    onCalendarMonthChange,
    disablePast,
    disabled,
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Grid item container className='ss-container-gap ss-from-to-date-picker'>
        <Grid item xs={12} md>
          <AvailabilityDatePicker
            {...sharedProps}
            label="From"
            className="ss-date-from w-100"
            heading="Select start date"
            value={startDate}
            onChange={setFromDate}
          />
        </Grid>
        {
          showEndDate && <Grid item xs={12} md>
            <AvailabilityDatePicker
              {...sharedProps}
              label="To"
              className="ss-date-to w-100"
              heading="Select end date"
              subheading={endSubheading}
              value={endDate}
              onChange={setToDate}
              minDate={moment(minToDate ?? fromDate, DateFormats.DAY)}
            />
          </Grid>
        }
      </Grid>
    </LocalizationProvider>
  );
};

export default AvailabilityFromToDatePicker;
